import * as Yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import RouteHelpers from 'helpers/routes';
import { forgotPassword } from 'actions/users';
import { useSelector } from 'react-redux';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Browser from 'helpers/browser';
import { Actions, Errors, Instructions, LinkWrapper } from './Styled';
import { Formik } from 'components/EnhancedForm';
import { Button, Text, TextField } from '@teachstonellc/component-library';
import FocusFirstInvalidField from 'components/FocusFirstInvalidField';
import { toastSuccess } from 'components/Toast';
import { normalizeLoginInput } from 'components/LoginManager/loginInput';
import useResendCooldown from 'hooks/useResendCooldown';

const validationSchema = Yup.object({
  email: Yup.string()
    .transform(normalizeLoginInput)
    .email(() => i18n.ft(messages.errors.email.invalid))
    .required(() => i18n.ft(messages.errors.email.required))
});

const ForgotPasswordForm = props => {
  const navigate = useNavigate();
  const location = useLocation();
  const [errors, setErrors] = useState();
  const [email, setEmail] = useState();
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { canResend, secondsLeft, startCooldown } = useResendCooldown();
  const currentUser = useSelector(state => state.user);
  const emailInputRef = useRef(null);
  const confirmationRef = useRef(null);
  const hasChangedViewRef = useRef(false);
  const loginPath = Browser.containsCookie('course_merchant')
    ? RouteHelpers.getPath('users-cm-login')
    : RouteHelpers.getPath('users-login');
  const isForgotPassword =
    window.location.pathname === RouteHelpers.getPath('users-forgot-password');

  function instructions() {
    return isForgotPassword
      ? i18n.ft(messages.resetInstructions)
      : i18n.ft(messages.setInstructions);
  }

  const initialValues = {
    email: normalizeLoginInput(location.state?.email)
  };

  useEffect(() => {
    // Redirect user to dashboard if already logged in
    if (currentUser.id) {
      navigate(RouteHelpers.getPath('app'), { replace: true });
    }
  }, [currentUser.id, navigate]);

  useEffect(() => {
    if (!hasChangedViewRef.current) {
      return;
    }

    if (success) {
      confirmationRef.current?.focus();
    } else {
      emailInputRef.current?.focus();
    }
  }, [success]);

  function sendEmail(emailToSend, onSent) {
    setLoading(true);
    setErrors(undefined);
    startCooldown();

    forgotPassword({ user: { email: emailToSend } })
      .then(() => {
        setLoading(false);
        hasChangedViewRef.current = true;
        setSuccess(true);
        setEmail(emailToSend);
        onSent?.();
      })
      .catch(({ response }) => {
        setLoading(false);
        setErrors(i18n.ft(messages.errors.unknown));
      });
  }

  function handleSubmit(values, { setFieldValue }) {
    const cleanEmail = normalizeLoginInput(values.email);
    setFieldValue('email', cleanEmail, false);
    sendEmail(cleanEmail);
  }

  function handleResend() {
    if (!canResend) {
      return;
    }

    sendEmail(email, () =>
      toastSuccess({ description: i18n.ft(messages.resent) })
    );
  }

  function handleUseDifferentEmail() {
    hasChangedViewRef.current = true;
    setSuccess(false);
    setErrors(undefined);
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {formik => {
        const isError = Boolean(formik.touched.email && formik.errors.email);

        return (
          <form
            noValidate
            onSubmit={formik.handleSubmit}
            onReset={formik.handleReset}
          >
            <FocusFirstInvalidField
              submitCount={formik.submitCount}
              fields={[{ isInvalid: isError, inputRef: emailInputRef }]}
            />

            <Text variant="h2" className="text-left pb-2">
              {isForgotPassword
                ? i18n.ft(messages.forgotYourPassword)
                : i18n.ft(messages.enterEmail)}
            </Text>

            {errors && <Errors>{errors}</Errors>}

            {success ? (
              <React.Fragment>
                <Instructions
                  ref={confirmationRef}
                  tabIndex={-1}
                  className="focus:outline-none"
                >
                  {i18n.ft(messages.confirmationSentTo)}{' '}
                  <span className="font-bold break-all">{email}</span>
                </Instructions>
                <Instructions className="mt-2">
                  {i18n.ft(messages.confirmationHelp)}
                </Instructions>

                <Actions className="mt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    shape="rounded"
                    disabled={loading}
                    onClick={handleUseDifferentEmail}
                  >
                    {i18n.ft(messages.useDifferentEmail)}
                  </Button>

                  <Button
                    type="button"
                    variant="secondary"
                    shape="rounded"
                    loading={loading}
                    disabled={loading}
                    aria-disabled={!canResend || undefined}
                    onClick={handleResend}
                  >
                    {canResend
                      ? i18n.ft(messages.resend)
                      : i18n.ft(messages.resendCountdown, {
                          seconds: secondsLeft
                        })}
                  </Button>
                </Actions>

                <LinkWrapper>
                  <Button
                    fullWidth
                    variant="primary"
                    shape="rounded"
                    loading={loading}
                    disabled={loading}
                    as={Link}
                    to={loginPath}
                  >
                    {i18n.ft(messages.backToLogin)}
                  </Button>
                </LinkWrapper>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Instructions
                  dangerouslySetInnerHTML={{
                    __html: instructions()
                  }}
                />

                <TextField
                  {...formik.getFieldProps('email')}
                  ref={emailInputRef}
                  type="email"
                  autoComplete="email"
                  label={i18n.ft(messages.email)}
                  placeholder={i18n.ft(messages.email)}
                  aria-invalid={isError}
                  error={isError ? formik.errors.email : undefined}
                />

                <Actions className="mt-4">
                  <Button
                    as={Link}
                    to={RouteHelpers.getPath('users-login')}
                    variant="secondary"
                    shape="rounded"
                  >
                    {i18n.ft(messages.cancel)}
                  </Button>

                  <Button
                    type="submit"
                    variant="primary"
                    shape="rounded"
                    loading={loading}
                    disabled={loading}
                  >
                    {i18n.ft(messages.send)}
                  </Button>
                </Actions>
              </React.Fragment>
            )}
          </form>
        );
      }}
    </Formik>
  );
};

export default ForgotPasswordForm;
